import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * A section of a screen that folds away, and remembers that it did.
 *
 * Every page in the app is a stack of these. On a phone the app is one narrow column
 * and a page like the plan is several screens of scrolling, so the thing a learner
 * came for is usually below the fold of something they have already read — folding is
 * how a page stays navigable without anything being taken off it.
 *
 * **`<details>` rather than state and a button**, and the reasons are all things a
 * hand-rolled version gets wrong. The element is focusable, toggles on Enter *and*
 * Space, and announces itself as "collapsed" or "expanded" to a screen reader with no
 * `aria-expanded` to keep in step with anything; browser find-in-page opens a closed
 * one to show the match; and printing expands them. The only thing we take away is the
 * native triangle, which is a few pixels wide and on a phone is a miss and a scroll
 * rather than an open — the whole summary row is the tap target instead, which is the
 * same decision `.history` already made.
 *
 * **Open by default.** A page whose sections all arrive shut is a page that looks
 * empty, and the learner's first act on every visit is to tap four things open to find
 * out what is on it. The exceptions are stated one at a time at the call site — the
 * research document because it is tens of thousands of words, the delete panel because
 * it is the one irreversible control in the app and should not be one mis-tap away
 * from somebody scrolling their profile.
 *
 * **The choice persists**, per section, in this browser. A learner who folds the plan
 * away because they only ever come here for the questions has said something durable,
 * and a screen that forgets it every visit is asking them to say it again forever. It
 * is deliberately per-browser and not stored on the account: it is a statement about
 * this device's screen, and it must not cost a round trip on a page load.
 */
const REMEMBERED = 'nabu.open.';

function remembered(id: string, fallback: boolean): boolean {
  try {
    const stored = localStorage.getItem(REMEMBERED + id);
    if (stored === 'open') return true;
    if (stored === 'shut') return false;
  } catch {
    // Site data blocked, or a private window that throws on the read rather than
    // answering null. A remembered fold is a convenience; the page still renders.
  }
  return fallback;
}

export default function Disclosure({
  id,
  anchor = id,
  title,
  aside,
  open: initiallyOpen = true,
  repeatsTitle = false,
  children,
}: {
  /** Stable across renames of the copy: it is the storage key. */
  id: string;
  /**
   * What this section is called in a URL, when a link points at it (F-143).
   *
   * Separate from `id` because the two are read by different people. `id` is a storage key
   * and must never change, or a learner's folded sections silently spring open; `anchor`
   * is part of an address somebody sees and may read out, so it is copy and is allowed to
   * be the learner's word rather than the code's — `#notifications`, not `#push`.
   *
   * Defaults to `id`, so every section is addressable and only the ones something links to
   * need to think about it.
   */
  anchor?: string;
  title: string;
  /** A count, a pill or a hint, shown on the summary row beside the title. */
  aside?: React.ReactNode;
  open?: boolean;
  /**
   * The content renders its own copy of this heading, so hide it.
   *
   * True wherever a whole existing panel is wrapped — `Sources`, `Schedule`, `Push`,
   * `Mail` and the rest each render an `<h2>` of their own, because each was written to
   * stand on a screen beside several others and each is still mounted that way
   * somewhere. Wrapped, that heading is the summary's word said twice in a row. It is
   * opt-in rather than automatic because a section whose body says something *different*
   * from its summary — the step panel, whose heading names the state the objective is
   * actually in — must keep it.
   */
  repeatsTitle?: boolean;
  children: React.ReactNode;
}) {
  // Read once, on mount. Re-reading on every render would fight the learner's own
  // toggle, and reading in an effect would paint the wrong state first.
  const [open, setOpen] = useState(() => remembered(id, initiallyOpen));
  const { hash } = useLocation();
  const sectionRef = useRef<HTMLElement>(null);
  const summaryRef = useRef<HTMLElement>(null);

  /**
   * A link that names this section opens it and goes to it (F-143).
   *
   * Leo: *"when i click on set it up for notifactions it just takes me to setting not
   * specifically to the place to set notificatons up."* A screen here is a stack of panels
   * several screens tall, so "go to the configuration screen" and "go to the switch" are
   * different instructions, and only one of them is what the notice offered.
   *
   * **Opened before it is scrolled to, and that is not optional.** A closed `<details>`
   * hides its contents with `display: none`; an element that is not rendered cannot be
   * scrolled to and cannot take focus, so both calls would be accepted and discarded — and
   * a learner who folded this section away last week would tap the link and see nothing
   * happen at all. The frame is what lets the opening paint before anything is measured.
   *
   * Focus moves with the scroll, for `panels.ts`'s reason: a jump that only moves the
   * viewport leaves a keyboard or screen-reader user where they were, having apparently
   * pressed something that did nothing.
   */
  useEffect(() => {
    if (hash !== `#${anchor}`) return;
    setOpen(true);
    const frame = requestAnimationFrame(() => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      summaryRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [hash, anchor]);

  const change = useCallback(
    (next: boolean) => {
      setOpen(next);
      try {
        localStorage.setItem(REMEMBERED + id, next ? 'open' : 'shut');
      } catch {
        // As above. The fold still works for this visit.
      }
    },
    [id],
  );

  return (
    <section
      id={anchor}
      ref={sectionRef}
      className={repeatsTitle ? 'panel disclosure-panel repeats' : 'panel disclosure-panel'}
    >
      <details className="disclosure" open={open} onToggle={(e) => change(e.currentTarget.open)}>
        <summary ref={summaryRef}>
          {/*
           * The section's heading lives on the summary, which is what makes the heading
           * itself the control. A heading beside a separate toggle gives a screen-reader
           * user two things to find for one section, and a sighted user a tap target
           * that looks like the whole row and is not.
           */}
          <h2>{title}</h2>
          {aside && <span className="muted small">{aside}</span>}
        </summary>
        {/*
         * The body is its own element so a section whose content decided it has nothing
         * to say can be hidden entirely — see `.disclosure-panel:has(...)` in styles.css.
         * `Mail` renders nothing at all until there is an address, and a collapsed
         * heading promising "Email to this address" above nothing is worse than silence.
         */}
        <div className="disclosure-body">{children}</div>
      </details>
    </section>
  );
}
